import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { getMe } from '@/api/account';

const usd = (microcents: number) =>
  `$${(microcents / 1_000_000).toFixed(4)}`;

const formatLimit = (cents: number) =>
  cents === 0 ? 'Unlimited' : `$${(cents / 100).toFixed(2)}`;

export function AccountScreen() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: getMe
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 text-nurev-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading account…
      </div>
    );
  }
  if (error || !data) {
    return (
      <p className="text-sm text-red-300">
        Couldn't load account. Try refreshing.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-nurev-text">Account</h1>
        <p className="mt-1 text-sm text-nurev-muted">
          Your NUREV Inference subscription, limits, and current spend.
        </p>
      </header>

      <section className="card">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
          Profile
        </h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <KV k="Name" v={data.name} />
          <KV k="Email" v={data.email} />
          {data.companyName && <KV k="Company" v={data.companyName} />}
          <KV k="Status" v={data.status} />
          <KV k="Billing plan" v={data.billingPlan} />
          <KV
            k="Member since"
            v={new Date(data.createdAt).toLocaleDateString()}
          />
        </dl>
      </section>

      <section className="card">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
          Spend &amp; limits
        </h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KV
            k="Month-to-date"
            v={`$${(data.monthlySpend / 100).toFixed(2)}`}
          />
          <KV k="Monthly limit" v={formatLimit(data.monthlyLimit)} />
          <KV
            k="Prepaid balance"
            v={`$${(data.prepaidBalance / 100).toFixed(2)}`}
          />
        </dl>
      </section>

      <section className="card">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
          Rate limits
        </h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <KV k="Requests / minute" v={data.rateLimitRpm.toLocaleString()} />
          <KV k="Tokens / minute" v={data.rateLimitTpm.toLocaleString()} />
        </dl>
        <p className="mt-4 text-xs text-nurev-muted">
          Rate limits are sliding-window and apply per API key. Need higher
          limits? Email{' '}
          <a
            href="mailto:support@nurev.io"
            className="text-nurev-purple-light hover:underline"
          >
            support@nurev.io
          </a>
          .
        </p>
      </section>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string | number }) {
  return (
    <div>
      <dt className="label">{k}</dt>
      <dd className="text-sm text-nurev-text">{v}</dd>
    </div>
  );
}

// Silence unused-export warning for `usd` (we want it available for future
// per-request views — keep here as a single-source-of-truth formatter).
export const _formatters = { usd };
